/**
 * ErrorBoundary — the app's last line of defence against a white screen.
 *
 * React unmounts the whole tree when a render throws, so one unexpected field
 * in an API payload (a null job title, say) used to leave the user staring at
 * a blank page with nothing to act on. This catches the error, keeps the stack
 * in the console for debugging, and offers a way back.
 */
import React from 'react';

type Props = { children: React.ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = { error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  public componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Unhandled render error', error, info.componentStack);
  }

  public render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className='min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center bg-white'>
        <div className='w-14 h-14 rounded-full bg-red-50 flex items-center justify-center'>
          <svg className='w-7 h-7 text-red-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z'
            />
          </svg>
        </div>
        <h1 className='text-xl font-semibold text-slate-900'>Something went wrong</h1>
        <p className='text-sm text-slate-500 max-w-md'>
          This page could not be displayed. Reloading usually helps; if it keeps happening,
          please let support know what you were doing.
        </p>
        <div className='flex items-center gap-3 pt-2'>
          <button
              type='button'
              onClick={() => window.location.reload()}
              className='py-2.5 px-6 rounded-full bg-[#050508] hover:bg-slate-800 text-white font-semibold text-sm transition-colors'
          >
            Reload
          </button>
          <button
              type='button'
              onClick={() => { window.location.href = '/home'; }}
              className='py-2.5 px-6 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold text-sm transition-colors'
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
